/**
 * Ground-nesting bird — the comic bodyguard of the Hubert lawn.
 *
 * Where the rabbit is shy and wanders, this bird is anchored: it owns a nest
 * and everything it does is measured from that spot. Walk near and it watches
 * you; walk too near and it storms over, scolds you, and struts back home.
 *
 * The joke only works if it is never actually threatening — there is no damage,
 * no knockback, no failure, and the bird always gives up and goes home.
 *
 * Pure module: it owns timing and position, the view owns pixels. Randomness
 * arrives through `rng` so tests can pin every decision.
 */

import type { SpawnPoint, WalkObstacle } from '@/world-prototype/types';
import {
  creatureDistance,
  stepToward,
} from '@/world-prototype/creatures/creatureGeometry';
import {
  pointInAnyObstacle,
  pointInPolygon,
} from '@/world-prototype/movement/walkArea';
import {
  isNearPoint,
  WILDLIFE_SLEEP_TIMING,
} from '@/world-prototype/creatures/wildlifeSleep';

/** Poses sliced from Bird-Poses.png, all drawn facing right. */
export type BirdPose =
  | 'idle'
  | 'watch'
  | 'peer'
  | 'walk'
  | 'hop'
  | 'peck'
  | 'charge'
  | 'defend'
  | 'sleep';

export type BirdPhase =
  /** Pottering about beside the nest. */
  | 'guard'
  /** Player is in the watch ring: standing tall, tracking them. */
  | 'alert'
  /** Player crossed the protect ring: wings up, about to go. */
  | 'windup'
  /** Storming toward the player. */
  | 'charge'
  /** Nose-to-shin scolding. */
  | 'peck'
  /** Strutting home. */
  | 'return'
  /** Heading back to the nest for the night. */
  | 'returnToNestForSleep'
  /** Curled on the nest until morning. */
  | 'sleeping'
  /** Brief wake beat before resuming guard duty. */
  | 'waking';

export type BirdFacing = 'left' | 'right';

export interface BirdYardConfig {
  yardId: string;
  /**
   * Ground position of the nest — the anchor for every radius below. Authored
   * as a fallback; the live value comes from the placed nest prop, which the
   * owner can drag in the World Editor.
   */
  nest: SpawnPoint;
  /** Grass the bird may stand on. Keeps it off scenery and out of the fence. */
  roamPolygon: readonly SpawnPoint[];
}

/** Rings around the nest, in world-width units. Forgiving on purpose. */
export const BIRD_RADIUS = {
  /** Player noticed: the bird stops pottering and stares. */
  watch: 0.2,
  /** Too close to the eggs: the bird comes at you. */
  protect: 0.105,
  /**
   * Victory line. Once provoked the bird keeps after the intruder until they
   * are this far from the nest — a proper run-you-off-the-lawn distance, not a
   * token step. Past it the bird declares the yard safe and heads home.
   */
  repel: 0.42,
  /**
   * Hard leash on the bird's own travel. Whatever the player does, the bird
   * never strays further than this from the eggs, so it cannot be lured across
   * the yard or into the scenery.
   */
  chase: 0.5,
  /** Close enough to deliver the telling-off. */
  peck: 0.05,
  /** How far it potters from the nest while guarding. */
  potter: 0.055,
} as const;

export const BIRD_TIMING = {
  /** Pause between idle fidgets. */
  potterMs: [1_600, 3_800],
  /** A single fidget hop. */
  hopMs: [260, 380],
  /** Beat between spotting the intruder and launching. */
  windupMs: [420, 700],
  /** Each scolding peck. */
  peckMs: [220, 320],
  /** Beat after the last peck before it turns for home. */
  peckHoldMs: [220, 360],
  /** Puffed-up beat between rounds of an ongoing chase. */
  regroupMs: [340, 560],
  /**
   * Breather once the intruder is clear, before the bird is willing to start
   * another chase. Stops a child who lingers at the edge of the nest from
   * getting a machine-gun of charges.
   */
  scoldCooldownMs: [1_400, 2_200],
  /** Standing guard again before it relaxes into pottering. */
  settleMs: [900, 1_500],
} as const;

/** Travel speeds in world-width units per second. */
export const BIRD_SPEED = {
  // Comfortably faster than a walking kid (yards run at 0.38) so a retreating
  // player actually gets harried rather than politely trailed.
  charge: 0.62,
  return: 0.22,
  potter: 0.13,
} as const;

/** Pecks delivered per telling-off. */
export const BIRD_PECKS = 2;

/**
 * How far in front of the player's feet the bird aims, in world Y.
 *
 * Depth is sorted by Y, so a bird that stops at the player's own Y ends up
 * drawn behind their legs and the entire telling-off happens out of sight. It
 * deliberately swings round to the camera side instead.
 */
export const BIRD_AIM_FORWARD = 0.03;

export interface BirdState {
  phase: BirdPhase;
  pose: BirdPose;
  x: number;
  y: number;
  facing: BirdFacing;
  /** Milliseconds left in the current phase. */
  timerMs: number;
  /** Where it is walking, or null when standing still. */
  target: SpawnPoint | null;
  /** Pecks left in this telling-off. */
  pecksLeft: number;
  /** Time left before it is willing to charge again. */
  cooldownMs: number;
  /** Small vertical lift, 0…1, for fidget hops. */
  liftT: number;
  /** Night tuck-in beside the nest. */
  sleepTarget: SpawnPoint | null;
}

export interface BirdStepContext {
  config: BirdYardConfig;
  /** Player ground position, or null when there is nobody to react to. */
  player: SpawnPoint | null;
  worldAspect: number;
  obstacles?: readonly WalkObstacle[];
  rng?: () => number;
}

/** Peak lift of a fidget hop, as a fraction of the bird's rendered height. */
export const BIRD_HOP_LIFT = 0.22;

/** Fresh bird, standing beside its nest. */
export function createBirdState(
  config: BirdYardConfig,
  rng: () => number = Math.random,
): BirdState {
  return {
    phase: 'guard',
    pose: 'idle',
    // Just off the nest rim, so the nest art is never hidden behind the bird.
    x: config.nest.x + BIRD_RADIUS.potter * 0.6,
    y: config.nest.y + 0.004,
    facing: 'left',
    timerMs: pickRange(BIRD_TIMING.potterMs, rng),
    target: null,
    pecksLeft: 0,
    cooldownMs: 0,
    liftT: 0,
    sleepTarget: null,
  };
}

export function isBirdSleepPhase(phase: BirdPhase): boolean {
  return (
    phase === 'returnToNestForSleep' ||
    phase === 'sleeping' ||
    phase === 'waking'
  );
}

export function requestBirdSleep(
  state: BirdState,
  target: SpawnPoint,
  worldAspect: number,
): void {
  state.target = null;
  state.pecksLeft = 0;
  state.cooldownMs = 0;
  state.liftT = 0;
  state.sleepTarget = target;

  if (isNearPoint(state, target, worldAspect)) {
    state.x = target.x;
    state.y = target.y;
    enterBirdSleep(state);
    return;
  }

  state.phase = 'returnToNestForSleep';
  state.pose = 'walk';
  state.target = { ...target };
  faceToward(state, target);
}

export function requestBirdWake(
  state: BirdState,
  config: BirdYardConfig,
): void {
  if (!isBirdSleepPhase(state.phase)) return;
  state.phase = 'waking';
  state.pose = 'idle';
  state.target = null;
  state.liftT = 0;
  state.sleepTarget = null;
  state.timerMs = WILDLIFE_SLEEP_TIMING.wakePauseMs;
  state.x = config.nest.x + BIRD_RADIUS.potter * 0.6;
  state.y = config.nest.y + 0.004;
  state.facing = 'left';
}

function enterBirdSleep(state: BirdState): void {
  state.phase = 'sleeping';
  state.pose = 'sleep';
  state.target = null;
  state.liftT = 0;
  state.timerMs = 0;
}

function stepBirdSleep(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
  dtSec: number,
): void {
  if (state.phase === 'sleeping') return;

  if (state.phase === 'waking') {
    state.timerMs -= dtSec * 1_000;
    if (state.timerMs <= 0) {
      state.phase = 'guard';
      state.pose = 'idle';
      state.timerMs = pickRange(BIRD_TIMING.potterMs, rng);
    }
    return;
  }

  if (state.target) {
    advanceWalk(state, ctx, BIRD_SPEED.return * dtSec);
    state.pose = 'walk';
    if (
      !state.target ||
      isNearPoint(state, state.sleepTarget!, ctx.worldAspect)
    ) {
      if (state.sleepTarget) {
        state.x = state.sleepTarget.x;
        state.y = state.sleepTarget.y;
      }
      enterBirdSleep(state);
    }
  }
}

/**
 * Tap / click the bird.
 *
 * From a distance this is just a poke to say hello: the bird snaps to attention
 * and hops in place, and the child has to actually walk over for the real
 * reaction. From inside the protect ring it does what standing there would have
 * done anyway, only immediately.
 */
export function pokeBird(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number = Math.random,
): void {
  if (isBirdSleepPhase(state.phase)) return;
  if (state.phase === 'charge' || state.phase === 'peck') return;

  const player = ctx.player;
  const nestDistance = player
    ? creatureDistance(ctx.config.nest, player, ctx.worldAspect)
    : Infinity;

  if (player && nestDistance <= BIRD_RADIUS.protect) {
    startWindup(state, player, rng);
    return;
  }

  state.phase = 'alert';
  state.pose = 'hop';
  state.target = null;
  state.liftT = 0;
  state.timerMs = pickRange(BIRD_TIMING.hopMs, rng);
  if (player) faceToward(state, player);
}

/** Advance the bird by `dtMs`. Mutates and returns the same object. */
export function stepBird(
  state: BirdState,
  dtMs: number,
  ctx: BirdStepContext,
): BirdState {
  const rng = ctx.rng ?? Math.random;
  const dt = Math.max(0, Math.min(dtMs, 120));
  const dtSec = dt / 1_000;

  if (isBirdSleepPhase(state.phase)) {
    stepBirdSleep(state, ctx, rng, dtSec);
    return state;
  }

  const player = ctx.player;
  const nestDistance = player
    ? creatureDistance(ctx.config.nest, player, ctx.worldAspect)
    : Infinity;

  state.timerMs -= dt;
  state.cooldownMs = Math.max(0, state.cooldownMs - dt);

  switch (state.phase) {
    case 'guard':
    case 'alert':
      stepGuarding(state, ctx, rng, dtSec, nestDistance);
      return state;
    case 'windup':
      if (state.timerMs <= 0) beginCharge(state, ctx);
      return state;
    case 'charge':
      stepCharge(state, ctx, rng, dtSec, nestDistance);
      return state;
    case 'peck':
      stepPeck(state, ctx, rng);
      return state;
    case 'return':
      stepReturn(state, ctx, rng, dtSec, nestDistance);
      return state;
    default:
      return state;
  }
}

function stepGuarding(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
  dtSec: number,
  nestDistance: number,
): void {
  const player = ctx.player;

  if (player && nestDistance <= BIRD_RADIUS.protect && state.cooldownMs <= 0) {
    startWindup(state, player, rng);
    return;
  }

  if (player && nestDistance <= BIRD_RADIUS.watch) {
    // Watching. It holds position and keeps its eye on the intruder rather than
    // pottering, which is what sells the "you are being monitored" beat.
    if (state.phase !== 'alert') {
      state.phase = 'alert';
      state.target = null;
      state.timerMs = pickRange(BIRD_TIMING.settleMs, rng);
    }
    state.pose = 'watch';
    state.liftT = 0;
    faceToward(state, player);
    return;
  }

  if (state.phase === 'alert') {
    // Intruder left the ring. Stay tall a moment longer, then relax.
    if (state.timerMs > 0) {
      state.pose = 'idle';
      return;
    }
    state.phase = 'guard';
    state.timerMs = pickRange(BIRD_TIMING.potterMs, rng);
  }

  if (state.target) {
    advanceWalk(state, ctx, BIRD_SPEED.potter * dtSec);
    state.pose = 'hop';
    // Tiny arc so a fidget step reads as a hop rather than a slide.
    state.liftT = state.target ? BIRD_HOP_LIFT : 0;
    if (!state.target) {
      state.liftT = 0;
      state.pose = 'idle';
      state.timerMs = pickRange(BIRD_TIMING.potterMs, rng);
    }
    return;
  }

  if (state.timerMs <= 0) startPotter(state, ctx, rng);
}

function startPotter(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
): void {
  const spot = pickPotterSpot(state, ctx, rng);
  state.timerMs = pickRange(BIRD_TIMING.potterMs, rng);
  if (!spot) {
    // Nowhere to step: just change what it is doing with its head.
    state.pose = rng() < 0.5 ? 'peer' : 'idle';
    return;
  }
  state.target = spot;
  state.pose = 'hop';
  faceToward(state, spot);
}

function startWindup(
  state: BirdState,
  player: SpawnPoint,
  rng: () => number,
): void {
  state.phase = 'windup';
  state.pose = 'defend';
  state.target = null;
  state.liftT = 0;
  state.pecksLeft = BIRD_PECKS;
  state.timerMs = pickRange(BIRD_TIMING.windupMs, rng);
  faceToward(state, player);
}

function beginCharge(state: BirdState, ctx: BirdStepContext): void {
  state.phase = 'charge';
  state.pose = 'charge';
  state.timerMs = 0;
  if (ctx.player) faceToward(state, ctx.player);
}

function stepCharge(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
  dtSec: number,
  nestDistance: number,
): void {
  const player = ctx.player;
  // Two ways the chase ends: the intruder is far enough off, or the bird has
  // run as far from the eggs as it is ever willing to go.
  if (
    !player ||
    nestDistance > BIRD_RADIUS.repel ||
    creatureDistance(state, ctx.config.nest, ctx.worldAspect) >
      BIRD_RADIUS.chase
  ) {
    startReturn(state, ctx, rng);
    return;
  }

  const aim = aimPoint(player, ctx);
  faceToward(state, aim);
  state.target = aim;
  advanceWalk(state, ctx, BIRD_SPEED.charge * dtSec);

  if (creatureDistance(state, aim, ctx.worldAspect) <= BIRD_RADIUS.peck) {
    state.phase = 'peck';
    state.pose = 'peck';
    state.target = null;
    state.timerMs = pickRange(BIRD_TIMING.peckMs, rng);
  }
}

function stepPeck(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
): void {
  // Face the player themselves, not the aim point it is standing on.
  if (ctx.player) faceToward(state, ctx.player);
  if (state.timerMs > 0) return;

  state.pecksLeft -= 1;
  if (state.pecksLeft > 0) {
    // Alternate peck and wings-up so the scolding has a rhythm to it.
    state.pose = state.pose === 'peck' ? 'defend' : 'peck';
    state.timerMs = pickRange(BIRD_TIMING.peckMs, rng);
    return;
  }

  const nestDistance = ctx.player
    ? creatureDistance(ctx.config.nest, ctx.player, ctx.worldAspect)
    : Infinity;

  if (nestDistance <= BIRD_RADIUS.repel) {
    // Still on the lawn it cares about. Puff up for a beat, then go again —
    // the bird keeps after you until you are properly clear of the nest.
    state.phase = 'windup';
    state.pose = 'defend';
    state.pecksLeft = BIRD_PECKS;
    state.target = null;
    state.timerMs = pickRange(BIRD_TIMING.regroupMs, rng);
    return;
  }

  state.pose = 'defend';
  state.phase = 'return';
  state.target = { ...nestStand(ctx.config) };
  state.timerMs = pickRange(BIRD_TIMING.peckHoldMs, rng);
  state.cooldownMs = pickRange(BIRD_TIMING.scoldCooldownMs, rng);
}

function startReturn(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
): void {
  state.phase = 'return';
  state.pose = 'walk';
  state.target = { ...nestStand(ctx.config) };
  state.timerMs = pickRange(BIRD_TIMING.settleMs, rng);
  faceToward(state, state.target);
}

function stepReturn(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
  dtSec: number,
  nestDistance: number,
): void {
  // A re-approach re-triggers the whole routine, which is the funniest possible
  // outcome and exactly what a child will try — but only once the breather has
  // run out, so the bird gets most of the way home first.
  if (
    ctx.player &&
    nestDistance <= BIRD_RADIUS.protect &&
    state.cooldownMs <= 0
  ) {
    startWindup(state, ctx.player, rng);
    return;
  }

  if (state.timerMs > 0 && state.pose === 'defend') return;

  state.pose = 'walk';
  if (!state.target) state.target = { ...nestStand(ctx.config) };
  advanceWalk(state, ctx, BIRD_SPEED.return * dtSec);

  if (!state.target) {
    state.phase = 'guard';
    state.pose = 'idle';
    state.liftT = 0;
    state.timerMs = pickRange(BIRD_TIMING.settleMs, rng);
  }
}

/** Move toward `state.target`, clearing it on arrival. */
function advanceWalk(
  state: BirdState,
  ctx: BirdStepContext,
  distance: number,
): void {
  const target = state.target;
  if (!target) return;

  const next = stepToward(state, target, distance, ctx.worldAspect);
  // Refuse illegal ground rather than sliding into the fence or a prop; the
  // bird simply stops short, which reads as it thinking better of it.
  if (isLegalSpot(next, ctx)) {
    state.x = next.x;
    state.y = next.y;
    faceToward(state, target);
  } else {
    state.target = null;
    return;
  }

  if (creatureDistance(state, target, ctx.worldAspect) <= 0.004) {
    state.target = null;
    state.liftT = 0;
  }
}

/** Legal grass: inside the roam polygon and clear of prop footprints. */
function isLegalSpot(point: SpawnPoint, ctx: BirdStepContext): boolean {
  // The owner can drag the nest anywhere in the World Editor, including outside
  // the authored roam polygon. Whatever it guards, the ground immediately
  // around the nest always counts as legal, or the bird would be stranded.
  const nearNest =
    creatureDistance(point, ctx.config.nest, ctx.worldAspect) <=
    BIRD_RADIUS.potter;
  if (!nearNest && !pointInPolygon(point.x, point.y, ctx.config.roamPolygon)) {
    return false;
  }
  if (
    ctx.obstacles &&
    ctx.obstacles.length > 0 &&
    pointInAnyObstacle(point.x, point.y, ctx.obstacles)
  ) {
    return false;
  }
  return true;
}

/** A fidget step: somewhere close to the nest, never on top of it. */
function pickPotterSpot(
  state: BirdState,
  ctx: BirdStepContext,
  rng: () => number,
): SpawnPoint | null {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  const nest = ctx.config.nest;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const angle = rng() * Math.PI * 2;
    const dist = BIRD_RADIUS.potter * (0.4 + rng() * 0.6);
    const candidate = {
      x: nest.x + Math.cos(angle) * dist,
      y: nest.y + Math.sin(angle) * dist * aspect,
    };
    if (!isLegalSpot(candidate, ctx)) continue;
    if (creatureDistance(state, candidate, ctx.worldAspect) < 0.012) continue;
    return candidate;
  }
  return null;
}

/**
 * Where the bird actually runs to: just in front of the player's feet, so the
 * scolding plays out on the camera side of them. Falls back to the player's own
 * spot if the near side is off the grass.
 */
function aimPoint(player: SpawnPoint, ctx: BirdStepContext): SpawnPoint {
  const forward = { x: player.x, y: player.y + BIRD_AIM_FORWARD };
  return isLegalSpot(forward, ctx) ? forward : { x: player.x, y: player.y };
}

/** Where the bird stands when it is home: beside the nest, not on it. */
function nestStand(config: BirdYardConfig): SpawnPoint {
  return {
    x: config.nest.x + BIRD_RADIUS.potter * 0.6,
    y: config.nest.y + 0.004,
  };
}

function faceToward(state: BirdState, point: SpawnPoint): void {
  if (Math.abs(point.x - state.x) < 0.002) return;
  state.facing = point.x < state.x ? 'left' : 'right';
}

function pickRange(range: readonly [number, number], rng: () => number): number {
  return range[0] + rng() * (range[1] - range[0]);
}
